import { debounce } from '@ts/utils';
import { AddressSearch } from "@ts/components/AddressSearch";
import { AnimatedButton } from '@ts/components/AnimatedButton';
import { UISwitch } from '@ts/components/UISwitch';
import { Modal } from '@ts/components/Modal';
import IMask from 'imask';
import { scrollToElement } from '@ts/utils';
import { validateFields } from "@js/_global/validation";
import { getStripeElements } from './payment';

let payNowBtn, 
    token,
    originalShippingValue, 
    originalTaxValue, 
    originalTotalValue,
    paymentProvider,
    paymentElements,
    isCheckingShipping = false,
    isTotalCleared = false;

const checkoutAPIEndpoints = {
    validateAddress: null,
    validateCheckoutUrl: null,
    verifyShippingRestrictions: null,
    taxesAndShipping: null,
    clearProvisionalFeesUrl: null,
    unlockCart: null
}

export const init = () => {
    setAddressSearchTools(); 
    setOriginalTaxShippingValues();
    setUpPaymentSystem();
    checkErrorMessage();
    setOrderSummaryHeader();
    setOrderSummaryFooter();    
    setCheckoutButton();
    setAddressSearch();
    setSimpleSwitch();
    setShippingAddressListeners();
    setZipCodeMask();
}

const checkErrorMessage = () => {
    const errorMessageContainer = document.getElementById('error-message-modal');
    if (errorMessageContainer && errorMessageContainer.textContent.trim()) {
        Modal.createAlertModal({
            title: 'Error',
            message: errorMessageContainer.textContent.trim()
        });
    }
}

const setUpPaymentSystem = async () => {
    const elements = await getStripeElements();
    if (!elements) return;
    paymentProvider = elements.paymentProvider;
    paymentElements = elements.paymentElements;
}

const setZipCodeMask = () => {
    const zipCodeInput = document.querySelector('.address-fields .zip input[type="text"]');
    if (zipCodeInput) {
        IMask(
            zipCodeInput, {
                mask: '00000[-0000]'
            }
        )
    }
}

const setOriginalTaxShippingValues = () => {
    const orderSummaryContainers = document.querySelectorAll('.order-summary-container');
    orderSummaryContainers.forEach(orderSummaryContainer => {
        const shippingCostElement = orderSummaryContainer.querySelector('.totals-row.shipping .totals-value');
        const taxElement = orderSummaryContainer.querySelector('.totals-row.taxes .totals-value');
        if (shippingCostElement) {
            originalShippingValue = shippingCostElement.innerHTML;
        }
        if (taxElement) {
            originalTaxValue = taxElement.innerHTML;
        }
        const totalElement = orderSummaryContainer.querySelector('.totals-row.total .totals-value');
        if (totalElement) {
            originalTotalValue = totalElement.innerHTML;
        }
    });
}

const setOrderSummaryHeader = () => {
    const orderSummaryContainer = document.querySelector('.order-summary-container.header');
    if (!orderSummaryContainer) return;

    const orderSummaryTrigger = orderSummaryContainer.querySelector('.order-summary-header');
    if (!orderSummaryTrigger) return;

    orderSummaryTrigger.addEventListener('click', () => {
        toggleOrderSummary(orderSummaryContainer);
    });
}

const setOrderSummaryFooter = () => {
    const orderSummaryContainer = document.querySelector('.order-summary-container.footer');
    if (!orderSummaryContainer) return;
    const orderSummaryTrigger = orderSummaryContainer.querySelector('.order-summary-header');
    if (!orderSummaryTrigger) return;
    
    orderSummaryTrigger.addEventListener('click', () => {
        toggleOrderSummary(orderSummaryContainer);
    });
}

const toggleOrderSummary = (orderSummaryContainer) => {
    const orderSummaryContents = orderSummaryContainer.querySelector('.order-summary-contents');
    const isExpanded = orderSummaryContainer.getAttribute('aria-expanded') === 'true';
    orderSummaryContainer.setAttribute('aria-expanded', !isExpanded);
    const chevron = orderSummaryContainer.querySelector('.fa-chevron-down, .fa-chevron-up');
    if (chevron) {
        chevron.classList.toggle('fa-chevron-down', isExpanded);
        chevron.classList.toggle('fa-chevron-up', !isExpanded);
    }
}

const setCheckoutButton = () => {
    payNowBtn = AnimatedButton.getButton('checkout-btn');
    if (!payNowBtn) return;
    payNowBtn.setOnClick(handleSubmitCheckout);
}

const checkPaymentStatus = async (form) => {
    const clientSecret =
        form.querySelector('#payment-area')?.dataset.clientSecret;

    if (!clientSecret) return 'unknown';

    try {
        const { paymentIntent, error } =
            await paymentProvider.retrievePaymentIntent(clientSecret);

        if (error || !paymentIntent) return 'unknown';

        return paymentIntent.status;
    } catch {
        return 'unknown';
    }
};

const showPaymentError = async (message) => {
    Modal.createAlertModal({
        title: 'Payment Error',
        message: message || 'Your payment could not be completed. Please try again.'
    });
    releaseCartLock();
    payNowBtn.enable();
};

const handlePaymentStatus = async (form, returnUrl, errorMessage) => {
    const status = await checkPaymentStatus(form);

    if (status === 'succeeded') {
        window.location.href = returnUrl;
        return;
    }

    await showPaymentError(errorMessage);
    return false;
};

const submitStripePayment = async (form, returnUrl) => {
    let result;
    try {
        result = await paymentProvider.confirmPayment({
            elements: paymentElements,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
        });
    } catch {
        return handlePaymentStatus(form, returnUrl);
    }

    if (result.error) {
        return handlePaymentStatus(form, returnUrl, result.error.message);
    }
    window.location.href = returnUrl;
};

const releaseCartLock = async () => {
    if (!checkoutAPIEndpoints.unlockCart) return;
    try {
        const token = getToken(document.querySelector('.checkout-form form'));
        await fetch(checkoutAPIEndpoints.unlockCart, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "RequestVerificationToken": token
            },
            body: '{}'
        });
    } catch (error) {
        console.error("Error releasing cart lock:", error);
    }
}  

const capitalizeFirstLetter = (value) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const handleFormFieldError = (errors, form) => {
    let firstErrorField = null;
    errors.forEach(error => {
        const errorField = document.getElementById(
            capitalizeFirstLetter(error.name)
        );
        if (errorField) {
            if (!firstErrorField) firstErrorField = errorField;
            errorField.classList.add('input-validation-error');
            errorField.setAttribute('aria-invalid', 'true');

            const validationMessage = form.querySelector(
                `span[data-valmsg-for="${errorField.id}"]`
            );
            if (validationMessage) {
                validationMessage.classList.remove('field-validation-valid');
                validationMessage.classList.add('field-validation-error');
                validationMessage.textContent = error.reason;
            }
        }
    });
    if (firstErrorField) {
        jumpToError(firstErrorField);
    }
}

const validateCheckout = async (form) => {
    if (!checkoutAPIEndpoints.validateCheckoutUrl) return true;
    
    const token = getToken(form);
    try {
        const response = await fetch(checkoutAPIEndpoints.validateCheckoutUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "RequestVerificationToken": token
            },
            body: JSON.stringify({
                "ContactFirstName": form.querySelector('input[name="ContactFirstName"]')?.value,
                "ContactLastName": form.querySelector('input[name="ContactLastName"]')?.value,
                "ContactEmail": form.querySelector('input[name="ContactEmail"]')?.value,
                "ContactPhone": form.querySelector('input[name="ContactPhone"]')?.value,
                "ShippingFirstName": form.querySelector('input[name="ShippingFirstName"]')?.value,
                "ShippingLastName": form.querySelector('input[name="ShippingLastName"]')?.value,
                "AddressLine1": form.querySelector('input[name="AddressLine1"]')?.value,
                "AddressLine2": form.querySelector('input[name="AddressLine2"]')?.value,
                "City": form.querySelector('input[name="City"]')?.value,
                "State": form.querySelector('select[name="State"]')?.value,
                "ZipCode": form.querySelector('input[name="ZipCode"]')?.value,
                "InvalidAddressOverride": form.querySelector('input[name="InvalidAddressOverride"]')?.value?.toLowerCase() === "true",
                "EncouragementNote": form.querySelector('textarea[name="EncouragementNote"]')?.value,
                "DisplayNamePublicly": form.querySelector('input[name="DisplayNamePublicly"]')?.checked || false
            })
        });

        if (!response.ok) {
            const responseData = await response.json();
            releaseCartLock();
            const errors = responseData.errors || [];
            if (errors) handleFormFieldError(errors, form);
            return false;
        }
    } catch (error) {
        console.error("Checkout validation error:", error);
        releaseCartLock();
        Modal.createAlertModal({
            title: 'Checkout Validation Error',
            message: error.message || 'An unexpected error occurred while validating your checkout information. Please try again.'
        });
        return false;
    }
    return true;
}

const handleSubmitCheckout = async (e) => {
    e.preventDefault();
    if (!paymentElements) return false;
    
    const form = document.querySelector('.checkout-form form');
    if (!form) return false;
    
    const isValid = validateFields(form);
    if (!isValid) return false;

    const paymentArea = document.getElementById('payment-area');
    if (!paymentArea) return false;
    
    const returnUrl = paymentArea?.dataset.returnUrl ?? window.location.href;
    
    payNowBtn.disable();
    
    const { error: submitError } = await paymentElements.submit();
    
    if (submitError) {
        scrollToElement(paymentArea);
        payNowBtn.enable();
        return false;
    }
    const isCheckoutValid = await validateCheckout(form);
    if (isCheckoutValid) submitStripePayment(form, returnUrl);
    else payNowBtn.enable();
    return false;
}

const clearFields = (container) => {
    const inputs = container.querySelectorAll("input[type='text']");
    inputs.forEach(input => { input.value = ""; });

    const suggestions = container.querySelector(".address-suggestions");
    if (suggestions) {
        suggestions.innerHTML = "";
        suggestions.style.display = "none";
    }
}

const setSimpleSwitch = () => {
    UISwitch.create(".switch");
}

// Address Change Listeners

const isShippingAddressEmpty = (addressData) => {
    return !(
        addressData.AddressLine1?.trim() ||
        addressData.AddressLine2?.trim() ||
        addressData.City?.trim() ||
        addressData.State?.trim() ||
        addressData.ZipCode?.trim()
    );
};

const isShippingAddressComplete = (addressData) => {
    return Boolean(
        addressData.AddressLine1?.trim() &&
        addressData.City?.trim() &&
        addressData.State?.trim() &&
        addressData.ZipCode?.trim()
    );
};

const getShippingAddressFields = (container) => {    
    return container.querySelectorAll(`
        .address1 input[type="text"],
        .city input[type="text"],
        .state select,
        .zip input[type="text"]
    `);
};

const isAnyShippingFieldFocused = (container) => {
  const fields = getShippingAddressFields(container);
  return Array.from(fields).some((f) => f === document.activeElement);
};

const setShippingAddressListeners = () => {
    const form = document.querySelector('.checkout-form form');
    if (!form) return;

    const addressFields = form.querySelector('.address-fields');
    if (!addressFields) return;

    const fields = getShippingAddressFields(addressFields);
    let userEditedShippingAddress = false;
    fields.forEach(field => {
        field.addEventListener('keydown', () => {
            userEditedShippingAddress = true;
        });

        field.addEventListener('paste', () => {
            userEditedShippingAddress = true;
        });

        field.addEventListener('change', () => {
            if (!userEditedShippingAddress) return;
            processShippingAddressChange(form);
        });

        field.addEventListener('blur', () => {
            if (!userEditedShippingAddress) return;
            processShippingAddressChange(form);
        });
    });
};

const processShippingAddressChange = debounce((form) => {
    const addressData = getShippingAddressData(form);
    const addressFields = form.querySelector('.address-fields');
    
    // Only check shipping if the address fields are visible.
    const addressFieldsContainer = form.querySelector('.shipping-address-container');
    if (!addressFieldsContainer || addressFieldsContainer.dataset.viewMode !== 'found') return;

    if (isAnyShippingFieldFocused(addressFields) || isShippingAddressEmpty(addressData)) {
        return;
    }

    if (!isShippingAddressComplete(addressData)) {
        resetShippingFees();
        validateShippingAddress(form, addressData);
        return;
    }
    startShippingChecks(form);
}, 1500);

// Address Validation 

const getShippingAddressData = (form) => {
    const addressData = {};
    addressData.AddressLine1 = form.querySelector('.address-fields .address1 input[type="text"]')?.value;
    addressData.AddressLine2 = form.querySelector('.address-fields .address2 input[type="text"]')?.value;
    addressData.City = form.querySelector('.address-fields .city input[type="text"]')?.value;
    addressData.State = form.querySelector('.address-fields .state select')?.value;
    addressData.ZipCode = form.querySelector('.address-fields .zip input[type="text"]')?.value;
    return addressData;
}

const resetShippingAddressFields = (form) => {
    const addressFields = form.querySelector('.address-fields');
    if (!addressFields) return;
    addressFields.querySelectorAll('input, select').forEach((field) => {
        const errorIndicator = field.nextElementSibling;
        if (!errorIndicator) return;
        errorIndicator.classList.remove("field-validation-error");
        errorIndicator.classList.add("field-validation-valid");
        errorIndicator.textContent = "";
    });
}

// Shipping Checks

const startShippingChecks = async (form) => {

    // Only check shipping if the address fields are visible.
    const addressFieldsContainer = form.querySelector('.shipping-address-container');
    if (!addressFieldsContainer || addressFieldsContainer.dataset.viewMode !== 'found') return;

    if (isCheckingShipping) return;
    isCheckingShipping = true;

    try {
        payNowBtn.disable();
        let addressData = getShippingAddressData(form);
        
        const failures = {
            validation: false,
            restriction: false,
            taxesShipping: false
        }

        const validationResult = await validateShippingAddress(form, addressData);
        applyValidationDataToForm(form, validationResult);
        
        if (!validationResult?.isValid) {
            failures.validation = true;
        }

        // Update address data with Validated Values
        addressData = getShippingAddressData(form);
        const restrictionResult = await verifyShippingRestrictions(form, addressData);    
        if (!restrictionResult?.isValid) {
            failures.restriction = true;
            handleShippingRestrictionFailure(restrictionResult);
            return;
        }
    
        // Taxes and Shipping
        const taxesShippingResult = await processTaxesShipping(form, addressData);
        if (!taxesShippingResult?.success) {
            failures.taxesShipping = true;
            handleTaxesShippingFailure(taxesShippingResult);
            return
        }
    
        // Show validation modal if failed, but allow process to continue
        if (failures.validation) {
            handleShippingValidationFailure(validationResult);
        } else {
            handleShippingValidationSuccess(validationResult);
        }
        handleTaxesShippingSuccess(taxesShippingResult);
    } finally {
        isCheckingShipping = false;
    }
}   

/* Validate Shipping Address */

const handleValidationFailures = (errorData) => {
    const {title, status, errors} = errorData;
    const addressFields = document.querySelector('.address-fields');
    if (!addressFields) return;
    errors.forEach((error) => {
        const fieldName = error.name.charAt(0).toUpperCase() + error.name.slice(1);
        const fieldError = error.reason
        const field = addressFields.querySelector(`[name="${fieldName}"]`);

        if (field) {
            const errorIndicator = field.nextElementSibling;
            errorIndicator.classList.remove("field-validation-valid");
            errorIndicator.classList.add("field-validation-error");
            errorIndicator.textContent = fieldError;
        }
    });
    payNowBtn.disable();
}

const validateShippingAddress = async (form, addressData) => {
    const token = getToken(form);
    resetShippingAddressFields(form);
    const validationData = await AddressSearch.validateAddress(
        checkoutAPIEndpoints.validateAddress, 
        token,
        addressData,
        {
            onFail: async (errorData) => {
                handleValidationFailures(errorData);
            }
        });
    return validationData;
}   

const setInvalidShippingAddressIndicator = (state) => {
    const invalidAddressOverride = document.querySelector('input[name="InvalidAddressOverride"]');
    if (!invalidAddressOverride) return;
    invalidAddressOverride.value = state;
}

const handleShippingValidationSuccess = (validationData) => {
    setInvalidShippingAddressIndicator(false);
}

const handleShippingValidationFailure = (validationData) => {
    const { addressLine1, addressLine2, city, state, zipCode } = validationData;
    const invalidAddressModal = document.getElementById('invalid-address-modal');
    if (!invalidAddressModal) return;
    invalidAddressModal.querySelector('.address-1').innerHTML = addressLine1;
    invalidAddressModal.querySelector('.address-2').innerHTML = addressLine2;
    invalidAddressModal.querySelector('.city').innerHTML = city;
    invalidAddressModal.querySelector('.state').innerHTML = state;
    invalidAddressModal.querySelector('.zip-code').innerHTML = zipCode;
    
    const modalTitle = invalidAddressModal?.dataset.modalTitle;

    Modal.create('#invalid-address-modal', {
        modalTitle: modalTitle ?? 'Attention',
        modalSize: 'modal-md'
    });
    setInvalidShippingAddressIndicator(true);
}

/* Shipping Restrictions */

const verifyShippingRestrictions = async (form, addressData) => {
    const token = getToken(form);

    const restrictionData = await getShippingRestrictions(
        checkoutAPIEndpoints.verifyShippingRestrictions, 
        token,
        addressData);
    return restrictionData;
}

const getShippingRestrictions = async (validationEndpoint, token, addressData) => {
    if (!validationEndpoint) return false;

    try {
        const response = await fetch(validationEndpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "RequestVerificationToken": token
            },
            body: JSON.stringify(addressData),
        });

        if (!response.ok) {
            console.error("Shipping restrictions validation failed:", response.status);
            return false;
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error("Shipping restrictions validation error:", error);
        return false;
    }
}

const handleShippingRestrictionFailure = (restrictionData) => {
    Modal.createAlertModal({
        title: 'Notice',
        message: restrictionData.validationFailures?.join('<br>') || 'The address you entered is not valid. Please check the fields and try again.'
    });
    resetShippingFees();
    payNowBtn.disable();
}

/* Taxes and Shipping */

const getTaxesShipping = async (taxesAndShipping, token, addressData) => {
    if (!taxesAndShipping) return false;

    try {
        const response = await fetch(taxesAndShipping, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "RequestVerificationToken": token
            },
            body: JSON.stringify(addressData),
        });

        if (!response.ok) {
            console.error("Taxes and shipping validation failed:", response.status);
            return false;
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error("Taxes and shipping validation error:", error);
        return false;
    }
}

const processTaxesShipping = async (form, addressData) => {
    const token = getToken(form);
    const shippingFeesData = await getTaxesShipping(
        checkoutAPIEndpoints.taxesAndShipping, 
        token,
        addressData); 
    return shippingFeesData;  
}

const handleTaxesShippingFailure = (taxesShippingData) => {
    Modal.createAlertModal({
        title: 'Error',
        message: taxesShippingData.errorMessage
    });
    resetShippingFees();
    payNowBtn.disable();
}

const handleTaxesShippingSuccess = (taxesShippingData) => {
    updateShippingFees(taxesShippingData);    
    updateTotals();
    isTotalCleared = false;
    payNowBtn.enable();
}

const postClearProvisionalFees = async (form) => {
    const token = getToken(form);
    try {
        const response = await fetch(checkoutAPIEndpoints.clearProvisionalFeesUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "RequestVerificationToken": token
            },
            body: '{}'
        });

        if (!response.ok) {
            console.error("Clearing provisional fees failed:", response.status);
            return false;
        }

        return true;
    } catch (error) {
        console.error("Clearing provisional fees error:", error);
        return false;
    }
}

const resetShippingFees = async () => {
    if (isTotalCleared) return;

    const clearingFees = await postClearProvisionalFees(document.querySelector('.checkout-form form'));

    const orderSummaryContainers = document.querySelectorAll('.order-summary-container');
    orderSummaryContainers.forEach(orderSummaryContainer => {
        const shippingCostElement = orderSummaryContainer.querySelector('.totals-row.shipping .totals-value');

        if (shippingCostElement) {
            shippingCostElement.innerHTML = originalShippingValue || '$0.00';
        }
        const taxElement = orderSummaryContainer.querySelector('.totals-row.taxes .totals-value');
        if (taxElement) {
            taxElement.innerHTML = originalTaxValue || '$0.00';
        }   
        const totalElement = orderSummaryContainer.querySelector('.totals-row.total .totals-value');
        if (totalElement) {
            totalElement.innerHTML = originalTotalValue || '$0.00';
        }
    });
    isTotalCleared = true;
}

const updateShippingFees = (data) => {
    const { shippingFee, tax } = data;

    const shippingRows = document.querySelectorAll('.totals-row.shipping');

    shippingRows.forEach(shippingRow => {
        const isFlatShipping = shippingRow.dataset.hasFlatShippingFee !== "false";
        const shippingCostElement = shippingRow?.querySelector('.totals-value');

        if (shippingCostElement && !isFlatShipping) {
            shippingCostElement.textContent = `$${shippingFee.toFixed(2)}`;
        }
    });
    
    const taxElements = document.querySelectorAll('.totals-row.taxes .totals-value');
    taxElements.forEach(taxElement => {
        taxElement.textContent = `$${tax.toFixed(2)}`;
    });
}
const parseMoney = (el) => parseFloat(el?.textContent?.replace(/[^0-9.-]+/g, '')) || 0;

const updateTotals = () => {

    const orderSummaryContainers = document.querySelectorAll('.order-summary-container');
    orderSummaryContainers.forEach(orderSummaryContainer => {
        const subTotal = orderSummaryContainer.querySelector('.totals-row.subtotal .totals-value');
        const taxes = orderSummaryContainer.querySelector('.totals-row.taxes .totals-value');
        const shipping = orderSummaryContainer.querySelector('.totals-row.shipping .totals-value');
        const discounts = orderSummaryContainer.querySelector('.totals-row.discount-total .totals-value');
        const total = orderSummaryContainer.querySelector('.totals-row.total .totals-value');
        const summaryTotalValue = orderSummaryContainer.querySelector('.summary-total-value');
        const subTotalValue = parseMoney(subTotal) || 0;
        const taxesValue = parseMoney(taxes) || 0;
        const shippingValue = parseMoney(shipping) || 0;
        const discountsValue = parseMoney(discounts) || 0;
        const totalValue = subTotalValue + taxesValue + shippingValue + discountsValue;

        if (total && summaryTotalValue) {
            total.textContent = `$${totalValue.toFixed(2)}`;
            summaryTotalValue.textContent = `$${totalValue.toFixed(2)}`;
        }
    });

 
}

const applyValidationDataToForm = (form, validationData) => {
    const address1 = form.querySelector('.address-fields .address1 input[type="text"]').value = validationData.addressLine1;
    const address2 = form.querySelector('.address-fields .address2 input[type="text"]').value = validationData.addressLine2;
    const city = form.querySelector('.address-fields .city input[type="text"]').value = validationData.city;
    const state = form.querySelector('.address-fields .state select').value = validationData.state;
    const zip = form.querySelector('.address-fields .zip input[type="text"]').value = validationData.zipCode;
};

const setAddressSearchRequired = (container, isRequired) => {
    const addressSearchRequiredField = container.querySelector("#checkout-content .address-search-required");
    if (!addressSearchRequiredField) return;
    addressSearchRequiredField.value = isRequired ? "True" : "False";
}

const setAddressSearch = () => {
    const [addressSearch] = AddressSearch.create(".address-autocomplete", {
        apiKey: "195948433105312307",
        submitButtonSelector: "#save-earnings-btn",
        lookupAddressUrl: "https://us-autocomplete-pro.api.smarty.com/lookup?",
        suggestionDelay: 300
    }) || [];

    if (!addressSearch) {
        return;
    }

    addressSearch.onOpen(searchInput => handleAddressSearchOpen(searchInput));
    addressSearch.onSelect((data, searchInput) => handleAddressSelect(data, searchInput));
};

const handleAddressSearchOpen = (searchInput) => {
        const shippingAddressContainer = searchInput.closest(".shipping-address-container");
        if (!shippingAddressContainer) return;

        shippingAddressContainer.setAttribute("data-view-mode", "searching");

        const addressSuggestionRequired =
            shippingAddressContainer.querySelector(".address-search-required");
        if (addressSuggestionRequired && "value" in addressSuggestionRequired) {
            addressSuggestionRequired.value = "False";
        }
}

const handleAddressSelect = (data, searchInput) => {
    const { address1, address2, city, state, zip } = data;
    const shippingAddressContainer = searchInput.closest(".shipping-address-container");
    if (!shippingAddressContainer) return;
    const form = shippingAddressContainer.closest("form");
    if (!form) return;

    shippingAddressContainer.setAttribute("data-view-mode", "found");

    const addressLine1 = shippingAddressContainer.querySelector(".address1 input[type='text']");
    const addressLine2 = shippingAddressContainer.querySelector(".address2 input[type='text']");
    const cityInput    = shippingAddressContainer.querySelector(".city input[type='text']");
    const stateSelect  = shippingAddressContainer.querySelector(".state select");
    const zipInput     = shippingAddressContainer.querySelector(".zip input[type='text']");
    
    if (addressLine1) addressLine1.value = address1;
    if (addressLine2) addressLine2.value = address2;
    if (cityInput)    cityInput.value    = city;
    if (stateSelect)  stateSelect.value  = state;
    if (zipInput)     zipInput.value     = zip;     
    
    setAddressSearchRequired(shippingAddressContainer, false);
    startShippingChecks(form);
}

const getToken = (form) => {
    if (token) return token;
    const tokenField = form.querySelector('input[name="__RequestVerificationToken"]');
    token = tokenField ? tokenField.value : null;
    return token;
}

const setAddressSearchTools = () => {
    const checkoutBtn = document.getElementById('checkout-btn');
    if (!checkoutBtn) return;

    // Set API endpoints from data attributes
    checkoutAPIEndpoints.validateAddress = checkoutBtn.getAttribute('data-validate-address-url');
    checkoutAPIEndpoints.verifyShippingRestrictions = checkoutBtn.getAttribute('data-verify-shipping-restrictions-url');
    checkoutAPIEndpoints.taxesAndShipping = checkoutBtn.getAttribute('data-obtain-cart-fees-url');
    checkoutAPIEndpoints.clearProvisionalFeesUrl = checkoutBtn.getAttribute('data-clear-provisional-fees-url');
    checkoutAPIEndpoints.unlockCart = checkoutBtn.getAttribute('data-unlock-cart-url');
    checkoutAPIEndpoints.validateCheckoutUrl = checkoutBtn.getAttribute('data-validate-checkout-url');

    // Address Search Listeners
    document.addEventListener("click", (e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;

        const findAnotherLink = target.closest(".find-another-address a");
        if (findAnotherLink) {
            e.preventDefault();
            resetShippingFees();
            const shippingAddressContainer = findAnotherLink.closest(".shipping-address-container");
            if (!shippingAddressContainer) return;

            clearFields(shippingAddressContainer);
            shippingAddressContainer.setAttribute("data-view-mode", "search");

            setAddressSearchRequired(shippingAddressContainer, true);

            return;
        }

        const dontSeeLink = target.closest(".dont-see-address a");
        if (dontSeeLink) {
            e.preventDefault();
            resetShippingFees();
            const shippingAddressContainer = dontSeeLink.closest(".shipping-address-container");
            if (!shippingAddressContainer) return;
            clearFields(shippingAddressContainer);
            shippingAddressContainer.setAttribute("data-view-mode", "found");
            setAddressSearchRequired(shippingAddressContainer, false);
        }
    });
}